import is from '@sindresorhus/is';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { Thread } from '../../../data-contract/chat';
import { useApplicationFetchers } from '../../../fetchers/use-application-fetchers';
import { chatKeys } from '../../api/query-keys';
import { getThreadStatus, THREAD_STATUS_REFETCH_INTERVAL_MS } from '../../thread-status';

type UseChatThreadOptions = {
    readonly hasLiveStream?: boolean;
};

export const useChatThread = (
    threadId: string | undefined,
    options?: UseChatThreadOptions,
): UseQueryResult<Thread, Error> => {
    const { fetchChatThread } = useApplicationFetchers();

    return useQuery({
        enabled: !is.undefined(threadId),
        queryFn: async ({ signal }) => {
            if (is.undefined(threadId)) {
                throw new Error('threadId required');
            }

            const result = await fetchChatThread(threadId, { signal });

            if (result.isErr === true) {
                throw result.error;
            }

            return result.value;
        },
        queryKey: chatKeys.thread(threadId ?? ''),
        // Only 'running' changes server-side without a user action; needs-input/on-hold must not keep polling.
        refetchInterval: (query) =>
            !is.undefined(query.state.data) &&
            getThreadStatus(query.state.data, { hasLiveStream: options?.hasLiveStream }) === 'running'
                ? THREAD_STATUS_REFETCH_INTERVAL_MS
                : false,
    });
};
